import { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { formatTime } from "../../helpers/global";
import playIcon from "../../assets/icons/play.svg";
import pauseIcon from "../../assets/icons/pause.svg";
import classes from "./AudioPlayer.module.css";

const AudioPlayer = (props) => {
  const { src, className } = props;
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [audioDuration, setAudioDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0); // in seconds
  const [currentTimelineSpot, setCurrentTimelineSpot] = useState(0);
  const audioPlayerContainerRef = useRef();
  const audioRef = useRef();
  const seekSliderRef = useRef();
  let raf = null;

  const audioPlayerToggleHandler = () => {
    if (isAudioPlaying) {
      audioRef.current.pause();
      cancelAnimationFrame(raf);
    } else {
      audioRef.current.play();
      requestAnimationFrame(whilePlaying);
    }
    setIsAudioPlaying((prevState) => !prevState);
  };

  const whilePlaying = () => {
    const seekSliderValue = Math.floor(audioRef.current.currentTime);
    setCurrentTimelineSpot(seekSliderValue);
    setCurrentTime(seekSliderValue);
    audioPlayerContainerRef.current.style.setProperty(
      "--seek-before-width",
      `${(seekSliderValue / audioDuration) * 100}%`
    );
    raf = requestAnimationFrame(whilePlaying);
  };

  const seekSliderInputHandler = (e) => {
    const { value, max } = e.target;
    audioPlayerContainerRef.current.style.setProperty(
      "--seek-before-width",
      (value / max) * 100 + "%"
    );
    setCurrentTime(value);
    if (!audioRef.current.paused) {
      cancelAnimationFrame(raf);
    }
  };

  const seekSliderChangedHandler = () => {
    audioRef.current.currentTime = seekSliderRef.current.value;
    if (!audioRef.current.paused) {
      requestAnimationFrame(whilePlaying);
    }
  };

  const audioEndHandler = () => {
    setIsAudioPlaying(false);
  };

  useEffect(() => {
    const audioElement = audioRef.current;

    const audioMetaDataLoadedHandler = () => {
      setAudioDuration(Math.floor(audioElement.duration));
    };

    if (audioElement) {
      if (audioElement.readyState > 0) {
        audioMetaDataLoadedHandler();
      } else {
        audioElement.addEventListener(
          "loadedmetadata",
          audioMetaDataLoadedHandler
        );
      }
    }

    return () => {
      if (audioElement) {
        audioElement.removeEventListener(
          "loadedmetadata",
          audioMetaDataLoadedHandler
        );
      }
    };
  }, []);

  return (
    <div
      className={classNames(classes["audio-player"], className)}
      ref={audioPlayerContainerRef}
    >
      <audio
        src={src}
        preload="metadata"
        ref={audioRef}
        className="hidden"
        onEnded={audioEndHandler}
      />
      <span
        className={classNames(
          classes["audio-player__current-time"],
          "numeric-content"
        )}
      >
        {formatTime(currentTime)}
      </span>
      <input
        ref={seekSliderRef}
        dir="ltr"
        type="range"
        max={audioDuration}
        value={currentTimelineSpot}
        onInput={seekSliderInputHandler}
        onChange={seekSliderChangedHandler}
        className={classes["audio-player__seek-slider"]}
      />
      <button
        type="button"
        className={classes["audio-player__play-btn"]}
        onClick={audioPlayerToggleHandler}
      >
        <img
          src={isAudioPlaying ? pauseIcon : playIcon}
          alt={isAudioPlaying ? "pause" : "play"}
        />
      </button>
    </div>
  );
};

export default AudioPlayer;

AudioPlayer.propTypes = {
  className: PropTypes.string,
  src: PropTypes.string,
};
